<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { DateTime } from 'luxon'
import { getCurrentUkTaxYear } from '@/helpers/ukTaxYear'
import { useManageAuthorisationModal } from '@/composables/useManageAuthorisationModal'
import { useRenewAuthorisationModal } from '@/composables/useRenewAuthorisationModal'
import { useSwiftaidAuthorisationStatus } from '@/composables/useSwiftaidAuthorisationStatus'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'

const { t } = useI18n()
const allowItems = ['0', '1'] as const

const store = useStore()
const { openManageAuthorisationModal } = useManageAuthorisationModal()
const { openRenewAuthorisationModal } = useRenewAuthorisationModal()

const authorisation = computed(() => store.state.donor?.authorisation ?? {})
const { isAuthorised } = useSwiftaidAuthorisationStatus()

const allowsOpen = ref(false)
const allowsTouched = ref(false)

watch(
  isAuthorised,
  (authorised) => {
    if (!allowsTouched.value) {
      allowsOpen.value = !authorised
    }
  },
  { immediate: true }
)

const taxYearLabel = computed(() => {
  const taxYear = getCurrentUkTaxYear()
  return `${taxYear.startYear}/${String(taxYear.endYear).slice(-2)}`
})

const authorisedSince = computed(() => {
  const authorised = authorisation.value?.authorised as string | undefined
  if (!authorised) return t('Dashboard.Common.NotSet')
  return DateTime.fromISO(authorised).toFormat('d MMMM yyyy')
})

const authorisedVia = computed(
  () =>
    (authorisation.value?.platform as string | undefined)?.trim() || 'Swiftaid'
)

const toggleAllows = () => {
  allowsTouched.value = true
  allowsOpen.value = !allowsOpen.value
}

const onPrimaryAction = () => {
  if (isAuthorised.value) {
    openManageAuthorisationModal()
    return
  }
  openRenewAuthorisationModal()
}
</script>

<template>
  <section
    class="sa-status-hero"
    :class="
      isAuthorised ? 'sa-status-hero--active' : 'sa-status-hero--inactive'
    "
    aria-labelledby="status-auth-heading"
  >
    <div class="sa-status-hero__top">
      <div class="sa-status-hero__icon" aria-hidden="true">
        <DashboardNavIcon
          :name="isAuthorised ? 'matching' : 'matching-inactive'"
        />
      </div>
      <div class="sa-status-hero__main">
        <h2 id="status-auth-heading" class="sa-status-hero__title">
          {{ $t('Dashboard.Status.Hero.Title') }}
        </h2>
        <p class="sa-status-hero__text">
          {{
            isAuthorised
              ? $t('Dashboard.Status.Hero.Body')
              : $t('Dashboard.Status.Hero.BodyInactive')
          }}
        </p>
      </div>
    </div>

    <dl class="sa-status-info-grid">
      <div class="sa-status-info-cell">
        <dt class="sa-status-info-label">
          {{ $t('Dashboard.Status.Hero.StatusLabel') }}
        </dt>
        <dd
          class="sa-status-info-value"
          :class="
            isAuthorised
              ? 'sa-status-info-value--active'
              : 'sa-status-info-value--inactive'
          "
        >
          {{
            isAuthorised
              ? $t('Dashboard.Status.Hero.StatusActive')
              : $t('Dashboard.Status.Hero.StatusInactive')
          }}
        </dd>
      </div>
      <div class="sa-status-info-cell">
        <dt class="sa-status-info-label">
          {{ $t('Dashboard.Status.Hero.AuthorisedVia') }}
        </dt>
        <dd class="sa-status-info-value">{{ authorisedVia }}</dd>
      </div>
      <div class="sa-status-info-cell">
        <dt class="sa-status-info-label">
          {{ $t('Dashboard.Status.Hero.AuthorisedSince') }}
        </dt>
        <dd class="sa-status-info-value">{{ authorisedSince }}</dd>
      </div>
      <div class="sa-status-info-cell">
        <dt class="sa-status-info-label">
          {{ $t('Dashboard.Status.Hero.TaxYear') }}
        </dt>
        <dd class="sa-status-info-value">{{ taxYearLabel }}</dd>
      </div>
    </dl>

    <div class="sa-status-hero__allows">
      <button
        type="button"
        class="sa-status-hero__allows-trigger"
        :aria-expanded="allowsOpen"
        aria-controls="status-auth-allows-panel"
        @click="toggleAllows"
      >
        <span>{{ $t('Dashboard.Status.Allows.Heading') }}</span>
        <svg
          class="sa-status-hero__allows-chevron"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>

      <div
        v-show="allowsOpen"
        id="status-auth-allows-panel"
        class="sa-status-hero__allows-panel"
      >
        <p class="sa-status-hero__allows-intro">
          {{
            isAuthorised
              ? $t('Dashboard.Status.Allows.Intro')
              : $t('Dashboard.Status.Allows.IntroInactive')
          }}
        </p>

        <ul class="sa-feature-list sa-status-hero__allows-list">
          <li v-for="item in allowItems" :key="item">
            <span class="sa-check-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
            {{ $t(`Dashboard.Status.Allows.Items.${item}`) }}
          </li>
        </ul>

        <div class="sa-status-callout sa-status-callout--info" role="note">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clip-rule="evenodd"
            />
          </svg>
          <p>
            <strong>{{ $t('Dashboard.Status.Allows.ScopeBold') }}</strong>
            {{ $t('Dashboard.Status.Allows.ScopeRest') }}
          </p>
        </div>
      </div>
    </div>

    <button
      type="button"
      :class="
        isAuthorised
          ? 'sa-btn-secondary sa-btn-unauthorise sa-status-hero__deactivate'
          : 'sa-btn-primary'
      "
      @click="onPrimaryAction"
    >
      {{
        isAuthorised
          ? $t('Dashboard.Status.Hero.DeactivateCta')
          : $t('Dashboard.Status.Hero.RenewCta')
      }}
    </button>
  </section>
</template>
